import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useForm, useWatch } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';
import {
  useCreatePracticeMutation,
  useOrganizationsShortQuery,
  usePartnerShortQuery,
} from '@common/cache';
import { createPracticeSchema } from '../schemas';
import { omit } from 'lodash';
import { formatApiError } from '@common/errors/error-formatting.ts';
import { fillAddressFormValues } from '@util/google-places';
import { checkFormIsDirty } from '@util/checkFormIsDirty.ts';

interface ICreatePracticeFormShape {
  name: string;
  organization_id?: string;
  partner_id: string;
  external: boolean;
  phone_number?: string;
  fax_number?: string;
  google_places_id: string;
  street: string;
  suite?: string;
  city: string;
  state: string;
  county?: string;
  zip_code: string;
}

const initialValues: Partial<ICreatePracticeFormShape> = {
  name: '',
  organization_id: '',
  partner_id: '',
  external: false,
  google_places_id: '',
  street: '',
  suite: '',
  city: '',
  state: '',
  county: '',
  zip_code: '',
  phone_number: '',
  fax_number: '',
};

export const useCreatePracticeModal = (
  isOpen: boolean,
  handleClose: () => void,
  handleOpenPracticeDetails: (id: string) => void,
) => {
  const { mutateAsync, isPending } = useCreatePracticeMutation();
  const { data: organizationOptions = [] } = useOrganizationsShortQuery();
  const [isSaveChangesModalOpen, setIsSaveChangesModalOpen] = useState(false);

  const formMethods = useForm<ICreatePracticeFormShape>({
    mode: 'onChange',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver<ICreatePracticeFormShape, any, any>(
      createPracticeSchema,
    ),
    defaultValues: {
      ...initialValues,
    },
  });

  const {
    handleSubmit,
    setValue,
    reset,
    getValues,
    formState: { isSubmitting, isValid, dirtyFields },
  } = formMethods;

  const isFormDirty = checkFormIsDirty(dirtyFields);

  const org_id = useWatch({
    control: formMethods.control,
    name: 'organization_id',
  });

  const { data: partnerOptions = [], refetch } = usePartnerShortQuery(
    org_id as string,
  );

  useEffect(() => {
    if (org_id) {
      refetch();
    }
  }, [org_id, isOpen]);

  const onCloseAction = () => {
    if (isFormDirty) {
      return setIsSaveChangesModalOpen(true);
    }
    handleClose();
    reset();
  };

  const handleSelectLocation = (place: Partial<google.maps.places.Place>) => {
    fillAddressFormValues({ setValue, place });
  };

  const submitHandler = async (values: ICreatePracticeFormShape) => {
    try {
      const response = await mutateAsync({
        ...omit(values, ['organization_id']),
        country: 'US',
        record_id: values.partner_id,
        phone_number: values.phone_number || '',
      });

      handleClose();
      reset();

      if (response.data.id) {
        handleOpenPracticeDetails(response.data.id);
      }
      toast.success('You added a new practice');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const onSubmit = handleSubmit(submitHandler);

  const handleCloseSaveChangesModal = () => {
    setIsSaveChangesModalOpen(false);
  };

  const handleDiscardChanges = () => {
    setIsSaveChangesModalOpen(false);
    handleClose();
    reset();
  };

  const handleSaveChanges = async () => {
    const values = getValues();

    try {
      await mutateAsync({
        ...omit(values, ['organization_id']),
        country: 'US',
        record_id: values.partner_id,
        phone_number: values.phone_number || '',
      });

      toast.success('You added a new practice');
      setIsSaveChangesModalOpen(false);
      handleClose();
      reset();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  return {
    formMethods,
    organizationOptions: organizationOptions.map(o => ({
      value: o.id,
      label: o.name,
    })),
    partnerOptions: partnerOptions.map(o => ({ value: o.id, label: o.name })),
    onSubmit,
    isSubmitting,
    submitHandler,
    handleSubmit,
    onCloseAction,
    setValue,
    isPartnerSelectDisabled: organizationOptions.length > 0 && !org_id,
    handleSelectLocation,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleSaveChanges,
    handleDiscardChanges,
    isValid,
    isEditSubmitting: isPending,
  };
};
